<template>
	<v-dialog v-model="dialog" persistent width="auto">
		<v-card style="width: 80vw; min-width: 750px; max-height: 90vh" id="mainCard" class="d-flex flex-column">
			<v-card-title class="card-header text-left fixed-card-title2">
				<v-row no-gutters class="d-flex justify-end align-center">
					{{ $t('monthly-report-header') }} ({{ monthYearSelected }})
					<v-spacer />
					<v-progress-circular indeterminate v-if="isExporting" />
					<v-icon v-else @click="exportToPDF()" :disabled="loading" class="mr-5 white-icon">mdi-cloud-download</v-icon>
					<v-icon color="white" @click="closeDialog">mdi-window-close</v-icon>
				</v-row>
			</v-card-title>
			<v-card-text class="pa-4 flex-grow-1 d-flex flex-column overflow-y-auto">
				<div id="content-to-print" class="flex-grow-1">
					<div class="my-5">
						<h3>Chronic Care Management (CCM)</h3>
					</div>
					<!-- Personal info -->
					<v-card class="section">
						<v-card-title>Personal info</v-card-title>
						<v-card-text>
							<table id="info">
								<tr>
									<td class="label">
										<b>{{ $t('patient-name') }}:</b> {{ currentRecord.patientName }}
									</td>
								</tr>
								<tr>
									<td class="label">
										<b>{{ $t('birthdate') }}:</b> {{ formatTimestamp(this.detailedUser?.userData.birthdate) }}
									</td>
								</tr>
								<tr>
									<td class="label">
										<b>{{ $t('date-of-service-header') }}:</b> {{ formatTimestamp(currentRecord.dateOfService) }}
									</td>
								</tr>
								<tr>
									<td class="label">
										<b>{{ $t('provider-name-header') }}:</b> {{ currentRecord.providerName }}
									</td>
								</tr>
								<tr>
									<td class="label">
										<b>{{ $t('ccm-coordinator-header') }}:</b> {{ currentRecord.coordinatorName }}
									</td>
								</tr>
								<tr>
									<td class="label">
										<b>{{ $t('time-spent-this-month-header') }}:</b> {{ formatDuration(timeSpentSecondsSelectedMonth) }}
									</td>
								</tr>
							</table>
						</v-card-text>
					</v-card>

					<!-- 1. Summary of Diagnoses (Two or More Chronic Conditions) -->
					<v-card class="section">
						<v-card-title>1. {{ $t('summary-of-diagnoses') }}</v-card-title>
						<v-card-text>
							<!-- Primary -->
							<h6 class="mb-3">{{ $t('primary-condition') }}</h6>
							<li v-if="primaryCondition" id="other-conditions" class="d-flex justify-space-between">
								<b>{{ primaryCondition.name }}</b>
								<label>
									<b>{{ $t('By') }}</b
									>: {{ primaryCondition.staffName }}, <b>{{ $t('At') }}</b
									>: {{ formatTimestamp(primaryCondition.conditionTimestampUTC) }}
								</label>
							</li>
							<li v-else>
								<b>{{ $t('no-data-available') }}</b>
							</li>
							<table id="primary-condition-history" class="mx-auto" v-if="primaryCondition?.conditionHistory?.length">
								<thead>
									<tr>
										<th>{{ $t('event-type') }}</th>
										<th>{{ $t('reason') }}</th>
										<th>{{ $t('staff') }}</th>
										<th>{{ $t('date') }}</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(item, index) in primaryCondition?.conditionHistory" :key="index">
										<td>{{ item.eventType }}</td>
										<td>{{ item.reason }}</td>
										<td>{{ item.performedBy }}</td>
										<td>{{ formatTimestamp(item.conditionHistoryTimestampUTC) }}</td>
									</tr>
								</tbody>
							</table>
							<!-- Other Chronic Conditions -->
							<h6 class="my-3">{{ $t('other-conditions') }}</h6>
							<div id="other-conditions-container" v-if="otherConditions?.length">
								<div v-for="(itemOtherCondition, index) in otherConditions" :key="index" class="my-3">
									<li id="other-conditions" class="d-flex justify-space-between">
										<b>{{ itemOtherCondition.name }}</b>
										<label
											><b>{{ $t('By') }}</b
											>: {{ itemOtherCondition.staffName }}, <b>{{ $t('At') }}</b
											>: {{ formatTimestamp(itemOtherCondition.conditionTimestampUTC) }}
										</label>
									</li>
									<table id="other-conditions-history" class="mx-auto" v-if="otherConditions.length">
										<thead>
											<tr>
												<th>{{ $t('event-type') }}</th>
												<th>{{ $t('reason') }}</th>
												<th>{{ $t('staff') }}</th>
												<th>{{ $t('date') }}</th>
											</tr>
										</thead>
										<tbody v-if="itemOtherCondition.conditionHistory?.length">
											<tr v-for="(item, index) in itemOtherCondition.conditionHistory" :key="index">
												<td>{{ item.eventType }}</td>
												<td class="reason-container">{{ item.reason }}</td>
												<td>{{ item.performedBy }}</td>
												<td>{{ formatTimestamp(item.conditionHistoryTimestampUTC) }}</td>
											</tr>
										</tbody>
										<tbody v-else>
											<tr>
												<td colspan="4">
													<span>{{ $t('no-data-available') }}</span>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
							<div v-else>{{ $t('no-other-conditions') }}</div>
						</v-card-text>
					</v-card>

					<!-- 2. Current Cognitive and Functional Status -->
					<v-card class="section">
						<v-card-title>2. {{ $t('current-cognitive-and-functional-status-header') }}</v-card-title>
						<v-card-text>
							<v-row>
								<v-col cols="12" v-for="(item, index) in functionalStatusData" :key="index">
									<v-card>
										<v-card-title>
											<h6>
												{{ item.name }}
												<v-chip v-if="item.isIndependent === true" color="green lighten-4" class="text-color-black">
													{{ $t('independent') }}
												</v-chip>
												<v-chip v-if="item.isIndependent === false" color="red lighten-4" class="text-color-black">
													{{ $t('dependent') }}
												</v-chip>
											</h6>
										</v-card-title>
										<v-card-text class="d-flex flex-row">
											<v-row>
												<v-col
													cols="4"
													v-for="(itemInside, indexInside) in item.items"
													:key="indexInside"
													class="text-left">
													<v-checkbox
														class="ma-0 pa-0"
														v-model="itemInside.flag"
														readonly
														hide-details
														:label="itemInside.value" />
												</v-col>
											</v-row>
										</v-card-text>
									</v-card>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>

					<!-- 3. Care Plan Summary  -->
					<v-card class="section">
						<v-card-title>3. {{ $t('care-plan-summary') }}</v-card-title>
						<v-card-text>
							<v-row v-if="carePlans?.length">
								<v-col sm="12" v-for="(item, index) in carePlans" :key="index">
									<v-card>
										<h6 class="d-flex justify-space-between px-2 pt-2">
											<p>{{ item.carePlanStatus }}</p>
											<p>{{ item.type }}</p>
										</h6>
										<v-card-text>
											<p>{{ item.description }}</p>
										</v-card-text>
										<v-card-actions class="d-flex justify-space-between">
											<div>
												<b>{{ $t('By') }}:</b> {{ item.performedBy }}
											</div>
											<div>
												<b>{{ $t('At') }}:</b> {{ formatTimestamp(item.date) }}
											</div>
										</v-card-actions>
									</v-card>
								</v-col>
							</v-row>
							<span v-else>{{ $t('no-data-available') }}</span>
						</v-card-text>
					</v-card>

					<!-- 4. Medication Review -->
					<v-card class="section">
						<v-card-title>4. {{ $t('medication-review') }}</v-card-title>
						<v-card-text>
							<v-row v-if="medicationEvents?.length">
								<v-col sm="12" v-for="(item, index) in medicationEvents" :key="index">
									<v-card>
										<h6 class="d-flex justify-space-between px-2 pt-2">
											<p>{{ item.medicationName }}</p>
											<p>({{ item.isMedicationHasEnded ? $t('finished') : $t('on-going') }})</p>
										</h6>
										<v-card-text>
											<p>{{ item.dosage }}</p>
										</v-card-text>
										<v-card-actions class="d-flex justify-space-between">
											<div>
												<b>{{ $t('By') }}:</b> {{ item.performedBy }}
											</div>
											<div>
												<b>{{ $t('At') }}:</b> {{ formatTimestamp(item.eventDate) }}
											</div>
										</v-card-actions>
									</v-card>
								</v-col>
							</v-row>
							<span v-else>{{ $t('no-data-available') }}</span>
						</v-card-text>
					</v-card>

					<!-- 5. Coordination with Providers -->
					<v-card class="section" ref="coordinationWithProviders">
						<v-card-title>5. {{ $t('coordination-with-providers') }}</v-card-title>
						<v-card-text>
							<v-textarea
								auto-grow
								rows="5"
								v-model="coordinationWithProviders"
								outlined
								:error-messages="!coordinationWithProviders.trim() ? $t('must-filled') : ''" />
						</v-card-text>
					</v-card>

					<!-- 6. Education and Support Provided -->
					<v-card class="section" ref="educationSupportProvided">
						<v-card-title>6. {{ $t('education-and-support-provided') }}</v-card-title>
						<v-card-text>
							<v-textarea
								auto-grow
								rows="5"
								v-model="educationSupportProvided"
								outlined
								:error-messages="!educationSupportProvided.trim() ? $t('must-filled') : ''" />
						</v-card-text>
					</v-card>

					<!-- 7. Social Determinants of Health -->
					<v-card class="section">
						<v-card-title>7. {{ $t('social-determinants-of-health-header') }}</v-card-title>
						<v-card-text>
							<v-row>
								<v-col cols="4" v-for="(item, index) in socialDeterminantsData" :key="index" class="text-left">
									<v-checkbox
										class="ma-0 pa-0"
										v-model="item.flag"
										readonly
										hide-details
										:label="item.value"
										:ripple="false" />
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>

					<!-- 8. Time Log -->
					<v-card class="section border">
						<v-card-title>8. {{ $t('time-logs-header') }}</v-card-title>
						<v-card-text>
							<table class="mx-auto time-log w-100">
								<thead>
									<tr>
										<th style="width: 10%">{{ $t('date') }}</th>
										<th style="width: 10%">{{ $t('time-spent') }}</th>
										<th style="width: 10%">{{ $t('entry-count') }}</th>
										<th style="width: 10%">{{ $t('performed-by') }}</th>
										<th style="width: 60%">{{ $t('list-activity-type') }}</th>
									</tr>
								</thead>
								<tbody v-if="ccmTimeLogsByPatientIdFiltered?.length" class="page-break-avoid">
									<tr v-for="(item, index) in ccmTimeLogsByPatientIdFiltered" :key="index" class="page-break-avoid">
										<td>{{ item.timeLogEntryDate }}</td>
										<td>{{ item.totalTimeSpentReadableFormat }}</td>
										<td>{{ item.timeLogEntryCount }}</td>
										<td>{{ item.performedByList.join(', ') }}</td>
										<td style="width: 60%">{{ item.activityTypeList.join(', ') }}</td>
									</tr>
								</tbody>
								<tbody v-else>
									<tr>
										<td colspan="5">
											<span>{{ $t('no-data-available') }}</span>
										</td>
									</tr>
								</tbody>
							</table>
						</v-card-text>
					</v-card>

					<!-- 9. Patient/Caregiver Consent -->
					<v-card class="section">
						<v-card-title>9. {{ $t('patient/caregiver-consent-header') }}</v-card-title>
						<v-card-text>
							<table>
								<thead>
									<tr>
										<th>{{ $t('consent-type-header') }}</th>
										<th>{{ $t('consent-text-header') }}</th>
										<th>{{ $t('file-name') }}</th>
										<th>{{ $t('obtained-by') }}</th>
										<th>{{ $t('recorded-at') }}</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(item, index) in ccmConsentForms" :key="index">
										<td>{{ item.consentType }}</td>
										<td>{{ item.consentText }}</td>
										<td>{{ item.fileName }}</td>
										<td>{{ item.obtainedBy }}</td>
										<td>{{ formatTimestamp(item.recordedAt) }}</td>
									</tr>
								</tbody>
							</table>
						</v-card-text>
					</v-card>

					<!-- 10. Plan Moving Forward -->
					<v-card class="section" ref="planMovingForward">
						<v-card-title>10. {{ $t('plan-moving-forward') }}</v-card-title>
						<v-card-text>
							<v-textarea
								rows="5"
								v-model="planMovingForward"
								outlined
								:error-messages="!planMovingForward.trim() ? $t('must-filled') : ''"></v-textarea>
						</v-card-text>
					</v-card>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatTimestamp, formatDuration } from '@/utils/utilities';
import html2pdf from 'html2pdf.js';

export default {
	name: 'CCMMonthlyReport',
	props: {
		monthYearSelected: {
			type: String,
			default: '',
		},
		dialog: {
			type: Boolean,
			default: false,
		},
	},
	data: function () {
		return {
			// Conditions
			primaryCondition: null,
			otherConditions: [],
			functionalStatusData: [],
			carePlans: [],
			medicationEvents: [],
			coordinationWithProviders: '',
			educationSupportProvided: '',
			patientCaregiverConsent: '',
			planMovingForward: '',
			socialDeterminantsData: [],
			ccmTimeLogsByPatientIdFiltered: [],
			timeSpentSecondsSelectedMonth: 0,
			selectedMonth: new Date().getMonth() + 1,
			selectedYear: new Date().getFullYear(),
			isExporting: false,
			loading: true,
			reportData: null,
			ccmConsentForms: [],
		};
	},
	components: {},
	async mounted () {
		this.localDialog = true;
		const [year, month] = this.monthYearSelected.split('-');
		this.selectedMonth = month;
		this.selectedYear = year;

		const responseAll = await this.getCCMReportDataForMonthByPatientId({
			patientId: this.detailedUser.userData.id,
			byMonth: this.selectedMonth,
			byYear: this.selectedYear,
			ccmRecordId: this.currentRecord.id,
		});
		this.reportData = responseAll.data[0];
		this.primaryCondition = this.reportData.condition.find((c) => c.isPrimary);
		this.otherConditions = this.reportData.condition.filter((c) => !c.isPrimary);
		this.functionalStatusData = JSON.parse(this.reportData.functionalStatus || '{}');
		this.carePlans = this.reportData.carePlan.filter((item) => {
			const itemDate = new Date(item.date);
			return (
				itemDate.getFullYear() === Number(this.selectedYear) && itemDate.getMonth() + 1 === Number(this.selectedMonth)
			);
		});
		this.medicationEvents = this.reportData.medication.filter((item) => {
			const itemDate = new Date(item.eventDate);
			return (
				itemDate.getFullYear() === Number(this.selectedYear) && itemDate.getMonth() + 1 === Number(this.selectedMonth)
			);
		});
		this.socialDeterminantsData = JSON.parse(this.reportData.socialDeterminants || '{}');
		this.ccmTimeLogsByPatientIdFiltered = this.reportData.timeLogEntryMonthlyAggregatedDate;
		this.ccmConsentForms = this.reportData.consentForms;
		this.loading = false;
	},
	computed: {
		...mapState({
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			diagnoses: (state) => state.ccmDiagnoses.ccmDiagnosesByPatientId,
			detailedUser: (state) => state.users.detailedUser,
			ccmConditionsByPatientId: (state) => state.ccmConditions.ccmConditionsByPatientId,
			ccmCarePlansByPatientId: (state) => state.ccmCarePlans.ccmCarePlansByPatientId,
			ccmMedicationEventByPatientId: (state) => state.ccmMedicationEvents.ccmMedicationEventByPatientId,
			ccmTimeLogsByPatientId: (state) => state.ccmTimeLogs.ccmTimeLogsByPatientId,
			ccmBillingByPatientId: (state) => state.ccmBilling.ccmBillingByPatientId,
			ccmVitalEntriesByPatientId: (state) => state.ccmVitalEntry.ccmVitalEntriesByPatientId,
			ccmConsentFormByPatientId: (state) => state.ccmConsentForms.ccmConsentFormByPatientId,
		}),
	},
	methods: {
		...mapActions({
			getCCMDiagnosesByPatientId: 'ccmDiagnoses/getCCMDiagnosesByPatientId',
			getCCMConditionsByPatientId: 'ccmConditions/getCCMConditionsByPatientId',
			getCCMCarePlansByPatientId: 'ccmCarePlans/getCCMCarePlansByPatientId',
			getCCMMedicationEventByPatientId: 'ccmMedicationEvents/getCCMMedicationEventByPatientId',
			getCCMTimeLogsByPatientId: 'ccmTimeLogs/getCCMTimeLogsByPatientId',
			getBillingByPatientId: 'ccmBilling/getCCMBillingByPatientId',
			getCCMVitalEntries: 'ccmVitalEntry/getCCMVitalEntriesByPatientId',
			getAllCCMConsentFormByPatientId: 'ccmConsentForms/getAllCCMConsentFormByPatientId',
			getFunctionalStatusTemplate: 'ccmRecords/getFunctionalStatusTemplate',
			getSocialDeterminantsTemplate: 'ccmRecords/getSocialDeterminantsTemplate',
			getCCMReportDataForMonthByPatientId: 'ccmRecords/getCCMReportDataForMonthByPatientId',
		}),
		closeDialog () {
			this.$emit('update:dialog', false);
		},
		formatTimestamp,
		formatDuration,
		exportToPDF () {
			if (!this.coordinationWithProviders.trim()) {
				const coordinationWithProviders = this.$refs.coordinationWithProviders?.$el;
				if (coordinationWithProviders && typeof coordinationWithProviders.scrollIntoView === 'function') {
					coordinationWithProviders.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			} else if (!this.educationSupportProvided.trim()) {
				const educationSupportProvided = this.$refs.educationSupportProvided?.$el;
				if (educationSupportProvided && typeof educationSupportProvided.scrollIntoView === 'function') {
					educationSupportProvided.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			} else if (!this.planMovingForward.trim()) {
				const planMovingForward = this.$refs.planMovingForward?.$el;
				if (planMovingForward && typeof planMovingForward.scrollIntoView === 'function') {
					planMovingForward.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			} else {
				this.isExporting = true;
				setTimeout(() => {
					const element = document.getElementById('content-to-print');
					element.classList.add('export-mode');

					html2pdf()
						.set({
							margin: 10,
							filename: 'RPM-monthly-report.pdf',
							image: { type: 'jpeg', quality: 0.98 },
							html2canvas: { scale: 2, useCORS: true },
							jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
							pagebreak: {
								mode: ['css'], // ‘css’, ‘legacy’, ‘whiteline’, or ‘avoid-all’
							},
						})
						.from(element)
						.save()
						.then(() => {
							element.classList.remove('export-mode');
							this.isExporting = false;
							this.closeDialog();
						})
						.catch(() => {
							// console.error('Error generating PDF:', error);
							element.classList.remove('export-mode');
							this.isExporting = false;
							this.closeDialog();
						});
				}, 100);
			}

			// element.classList.remove('export-mode');
		},
		async getFunctionalStatusData () {
			let rawData;
			try {
				rawData = JSON.parse(this.currentRecord.functionalStatus);
			} catch (e) {
				rawData = null;
			}
			if (!rawData) {
				const response = await this.getFunctionalStatusTemplate();
				rawData = response.data;
				rawData[0].independent = true;
				rawData[0].dependentOnAll = false;
				rawData[1].independent = true;
				rawData[1].dependentOnAll = false;
			}
			return rawData;
		},

		async getSocialDeterminantsData () {
			let rawData;
			try {
				rawData = JSON.parse(this.currentRecord.socialDeterminants);
			} catch (e) {
				rawData = null;
			}
			if (!rawData) {
				const response = await this.getSocialDeterminantsTemplate();
				return response.data;
			}
			return rawData;
		},
	},
};
</script>
<style scoped lang="scss">
@import '@/scss/base/variables';

/* Table styling */
table {
	width: 100%;
	border-collapse: collapse;

	tr:nth-child(odd) {
		background-color: $color-gray-3;
	}

	tr {
		vertical-align: baseline;
	}

	th,
	td {
		padding: 8px;
	}
}

.label {
	font-size: small;
}

/* Info table alignment */
#info {
	margin: 0 auto;

	tr {
		td:first-child {
			text-align: start;
			padding-right: 10px;
		}
	}
}

.reason-container {
	overflow-wrap: anywhere;
	width: 500px;
}

/* Styling for PDF rendering */
@media print {
	body {
		-webkit-print-color-adjust: exact !important;
		print-color-adjust: exact !important;
	}
}

/* Ensure breaks after each section if needed */
.v-card {
	margin: 0px 0px 20px 0px;
}

.v-card:first-of-type {
	margin: 0px;
}

.checkbox {
	color: white;
	font-weight: bold;
	letter-spacing: 1px;
	font-family: cursive;
	height: 25px;
	width: 25px;
	display: inline-block;
	padding: 0;
	border-radius: 5px;
	margin: 10px 5px 10px 10px;
}

.section {
	page-break-after: always;
	page-break-inside: avoid;
}

.section:first-of-type {
	page-break-after: avoid !important;
}

.section:last-of-type {
	page-break-after: auto !important;
}

.v-input--checkbox {
	page-break-inside: avoid !important;
	display: inline-block !important;
}

.checked {
	@extend .checkbox;
	background: $primary;
}

.unchecked {
	@extend .checkbox;
	border: 1px solid $primary;
}

.bottom-sticky {
	position: sticky;
	bottom: 10px;
}

.top-sticky {
	position: sticky;
	top: 0px;
	z-index: 1;
}

.export-mode {
	font-size: 65%;
	/* Global reduction to 85% of default */
	.v-card {
		box-shadow: none !important;
		border: 1px solid #ccc !important;
		background-color: white !important;
	}
	li {
		display: flex !important;
		justify-content: space-between !important;
	}
	.section {
		border-radius: 5px;
		margin: 10px 0px;
	}
	h1,
	.v-card-title {
		font-size: 0.8em;
		/* Still larger than body */
		font-weight: 300;
	}
	b {
		font-size: 0.75em;
		text-align: start;
	}
	p,
	td,
	th {
		font-size: 0.75em;
		text-align: left;
	}
	small {
		font-size: 0.6em;
	}
	.time-log {
		width: 100% !important;
	}
	.time-log td,
	.time-log th {
		font-size: 0.75em;
		text-align: left;
		width: auto !important;
		word-wrap: break-word;
	}
}

.primary--text,
v-input--checkbox,
type[checkbox] {
	color: #ff585f !important;
}

.space-between {
	display: flex !important;
	justify-content: space-between !important;
	width: 100%;
}

.mandatory-field {
	border: 1px solid red;
}

table,
p {
	text-align: left;
}
</style>
